<template>
  <page-loader v-if="loadingStatus === 'loading'" />
  <div v-else class="contain-section">
    <div class="row">
      <div class="col-md-5 col-lg-4">
        <div class="bg-white p-3 rounded-4 recent-ancestry-results overflow-auto">
          <h5 class="text-start">Your Recent Ancestry Results</h5>
          <PanelUpgradeOptIn />
          <div class="alert alert-info" v-if="starterTestMode">
            <p class="fs-14 text-start">
              Discover more about your ancestry by diving deeper into all your regions and
              subregions. With our full autosomal upgrade you won't miss out on exploring
              all {{ regionCount }} region{{ regionCount > 1 ? 's' : '' }} and uncovering
              insights from {{ subregionCount }} subregion{{ subregionCount > 1 ? 's' : '' }}.
              Even your smallest DNA contribution from a single region, at
              {{ autosomalStore.result.lowestPercentage }}%, holds a fascinating story waiting to
              be revealed.
            </p>
            <router-link :to="{ name: 'AncestryDetails', params: { slug: 'autosomal-upgrade' } }"
              class="fs-14 d-flex gap-2 no-underline">
              <feather-wrapper icon="zap" class="mr-2" />
              Upgrade now
            </router-link>
          </div>
          <div class="custom-accordian">
            <div class="accordion" id="accordionExample">
              <!-- <p @click="backToResult">backToResult</p> -->
              <div class="accordion-item" v-for="region in autosomalStore.result.regions" :key="region.id">
                <h2 class="accordion-header" :id="'heading-' + region.id">
                  <button class="accordion-button {{ region.id }}" type="button" :class="{
                    'open-background': activeAccordionId === region.id,
                  }" @click="toggleVisibility(region.id)">
                    <div class="custom-name">
                      <!-- <span><img :src="require('@/assets/images/ancetry/globicon.png')" alt="" /></span> -->
                      <span>{{ region.percentage }}%</span>
                      <label>{{ region.title }}</label>
                    </div>
                  </button>
                </h2>
                <div :id="region.id" class="collapse" :class="{ show: activeAccordionId === region.id }"
                  aria-labelledby="'heading-' + region.id
                    " data-bs-parent="#accordionExample">
                  <div class="px-4">
                    <ul class="p-2">
                      <li class="list-group" v-for="child in region.children" :key="child.id">
                        <div class="d-flex flex-column">
                          <div :class="{ 'active-item': activeItemId === child.id, 'list-items': child.title }"
                            class="d-flex align-items-baseline gap-2 text-start"
                            @click="navigateToMap(child, child.id)">
                            <div class="map-color-indicator rounded-circle" :style="`background-color:${child.colour}`">
                            </div>
                            <div class="fs-14">{{ child.title || "Unlock to discover this region" }} </div>
                            <div class="fs-14 ml-auto region-percentage">{{ child.percentage }}%</div>
                          </div>

                          <ul class="p-2">
                            <li class="list-group py-1" v-for="supChild in getChildrenToDisplay(child)"
                              :key="supChild.id">
                              <template v-if="supChild.title">
                                <div :class="{ 'active-item': activeItemId === supChild.id }"
                                  class="d-flex align-items-baseline gap-2 list-items text-start"
                                  @click="navigateToMap(supChild, supChild.id)">
                                  <div class="map-color-indicator rounded-circle"
                                    :style="`background-color:${supChild.colour}`"></div>
                                  <div class="fs-14">{{ supChild.title }} </div>
                                  <div class="fs-14 ml-auto region-percentage">{{ supChild.percentage ?? 0 }}%</div>
                                </div>
                              </template>
                              <div class="d-flex align-items-baseline gap-2 text-start text-secondary" v-else>
                                <div class="map-color-indicator rounded-circle p-1" style="background-color: #aaa">
                                </div>
                                <div class="fs-14">Unlock to discover this subregion</div>
                                <div class="fs-14 ml-auto text-end w-25 region-percentage">{{ supChild.percentage }}%
                                </div>
                              </div>
                            </li>
                            <div v-if="shouldLimitChildren(child)" class="fs-14">
                              + {{ child.children.length - 3 }} regions
                            </div>
                          </ul>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-7 col-lg-8">
        <div class="map-wrap">
          <div class="col-sm-12">
            <RecentAncestryMap :autosomal-result="autosomalStore?.result" v-model="selectedGroupId" 
              :key="profileBarcode"  @request-history-result="getHistoryResult" />
          </div>
          <div class="col-sm-12" v-if="details">
            <div class="card-section mt-4">
              <div class="col-sm-12 overview-hdr-dtl">
                <h6 class="mb-0">
                  {{ details.title }}
                  <!--  <label>
                    {{ details?.parentTitle ? "(" + details?.parentTitle + ")" : "" }}
                  </label> -->
                </h6>
                <span v-if="percentage" :class="{ 'fs-14': starterTestMode }">{{ percentage }}%</span>
              </div>
              <div class="alert alert-info" v-if="starterTestMode">
                <p class="fs-14 text-start">
                  Unlock this level of detailed insights across all regions and subregions when you
                  upgrade. Don't miss out on comprehensive information for every aspect of your
                  ancestry journey. Enhance your experience and dive deeper into your heritage.
                </p>
                <router-link :to="{ name: 'AncestryDetails', params: { slug: 'autosomal-upgrade' } }"
                  class="fs-14 d-flex gap-2 no-underline">
                  <feather-wrapper icon="zap" class="mr-2" />
                  Upgrade now
                </router-link>
              </div>
              
              <div class="col-sm-12">
                <figure>
                  <img 
                     :src="details?.images[0]" :alt="details?.title"
                    
                      class="img-fluid rounded-4" />
                </figure>
              </div>
              <div class="col-sm-12">
                <div class="flex items-start justify-between mb-1">
                  <!-- Additional content can go here -->
                </div>
                <h4 v-if="details?.parentTitle" class="text-gray-500 font-normal">
                  {{ details?.parentTitle }}
                </h4>
                <div class="prose flex text-start">
                  <div v-if="details?.shortDescription" class="recent-map single-column"
                    v-html="details?.shortDescription" />
                  <img v-if="details?.images[1]" :src="details?.images[1]" :alt="details?.title"
                    class="mb-4 img-fluid" />
                  <div v-if="details?.fullDescriptionOne" v-html="details?.fullDescriptionOne" />
                  <img v-if="details?.images[2]" :src="details?.images[2]" :alt="details?.title"
                    class="mb-4 img-fluid" />
                  <div v-if="details?.fullDescriptionTwo" v-html="details?.fullDescriptionTwo" />
                  <template v-if="details?.images.length > 3">
                    <img v-for="imageUrl in details?.images.slice(3)" :src="imageUrl" :alt="details?.title"
                      :key="imageUrl" class="mb-4 img-fluid" />
                  </template>
                  <template v-if="details?.citations.length">
                    <h4 class="text-grey-700 font-semibold mb-2">Citations</h4>
                    <ul class="ml-6 list-disc leading-tight mb-4 list-outside">
                      <li v-for="citation in details?.citations" :key="citation.title">
                        <a class="fs-14 italic" :href="citation.source" target="_new">{{ citation.title }}</a>
                      </li>
                    </ul>
                  </template>
                </div>
              </div>
             
            </div>
          </div>
          <div v-if="isLoading">
            <div class="loading-large" >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed,nextTick } from "vue";
import { useAutosomalStore } from "@/store/ancestry/autosomal";
import Autosomal from "@/services/autosomal";
import store from "@/store";
import RecentAncestryMap from "@/view/ancestry/recent/RecentAncestryMap.vue";
import PanelUpgradeOptIn from "@/view/ancestry/recent/PanelUpgradeOptIn.vue";
import { isEmpty } from "lodash";

const autosomalStore = useAutosomalStore();
const image = ref(false);
const details = ref();
const topResult = ref();
const loadingStatus = ref("loading");
const activeAccordionId = ref(null); // Track which accordion is currently open
//const imageLoaded  = ref(false);
const isLoading = ref(false);

const selectedGroupId = ref('');

const profileBarcode = computed(() => store.getters.profile.profile.barcode);

onMounted(async () => {
  if (isEmpty(autosomalStore.result)) {
    await autosomalStore.getAutosomalResults();
  }
  loadingStatus.value = "loaded";
  topResult.value = autosomalStore.result.regions[0].groupId;
  activeAccordionId.value  =  autosomalStore.result.regions[0].id;
  if (isEmpty(starterTestMode.value)) {
    await store.dispatch("SET_KIT_TESTS", profileBarcode.value);
  }
});

watch(() => profileBarcode.value,
  async () => {
    loadingStatus.value = "loading";
    await autosomalStore.getAutosomalResults();
    
    await store.dispatch("SET_KIT_TESTS", profileBarcode.value);
    details.value = null;
    loadingStatus.value = "loaded";

    
  })

const starterTestMode = computed(() => store.getters.starterTestMode);
// const onImageLoad = () => {
//   imageLoaded.value = true; // Once the image is fully loaded, set the flag to true
// };
const percentage = ref();
watch(() => selectedGroupId.value,
  groupId => {
    if (groupId) {
      const currentRegion = autosomalStore.combinedRegionsAndSubregions.find((item) => {
        return item.groupId === selectedGroupId.value;
      });
      percentage.value = currentRegion?.percentage;
      activeItemId.value = currentRegion.id ?? 0;
      
      document.querySelector('#map').scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  });
const toggleVisibility = (regionId) => {
  activeAccordionId.value = activeAccordionId.value === regionId ? null : regionId;
};

const getHistoryResult =async (groupId) => {

  image.value ='';
  details.value = null;
  try {
   isLoading.value = true;
    const res = await Autosomal.getHistory({
      barcode: store.getters["profile"].profile.barcode,
      groupId: groupId,
    });
    const something = res.testData[topResult.value];
 
    details.value = res.testData[groupId];
    image.value = something.images[0];
    //isLoading.value = false;
    await nextTick();
  } catch (err) {
    console.error('Error fetching data:', err);
  }
  isLoading.value = false;
};

// Load the top region's history once topResult is set (in onMounted). Guards against
// firing GET /tests/{barcode}/history/undefined before the initial result has loaded.
watch(
  topResult,
  (groupId) => {
    if (groupId) {
      getHistoryResult(groupId);
    }
  },
  { immediate: true }
);

const activeItemId = ref(null); // Track the currently active item

const navigateToMap = (item, id) => {
  if (item.groupId) {
    activeItemId.value = id; // Set the active item id based on the clicked item
    selectedGroupId.value = item.groupId;
    getHistoryResult(item.groupId);
  }
};

const shouldLimitChildren = region => starterTestMode.value && region.children.length > 3;

const getChildrenToDisplay = region => shouldLimitChildren(region) ? region.children.slice(0, 3) : region.children;

const regionCount = computed(() => {
  return autosomalStore.regions.length;
});

const subregionCount = computed(() => {
  return autosomalStore.subregions.length;
});
</script>

<style scoped lang="scss">
.loading-large {
  width: 100px;
  height: 100px;
  border: 6px solid #3B82F6;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;

  margin-top: 50px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.recent-ancestry-results {
  position: sticky;
  top: 120px;
  max-height: calc(100dvh - 150px);
  scrollbar-width: thin;
}

@media(max-width:990px) {
  .recent-ancestry-results {
    top: 80px;
  }
}

.map-color-indicator {
  width: 10px;
  height: 10px;
}

.ml-auto {
  margin-left: auto;
}

.active-item {
  color: #3377ff;
  font-weight: 600;
}

.recent-ancetry-wrapper .open-background::after {
  transform: rotate(0deg) !important;
}

.list-items {
  cursor: pointer;

  &:hover {
    color: #3377ff;
  }
}

.region-percentage {
  text-wrap: nowrap;
}

.italic {
  font-style: italic;
}
</style>
